import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
    Box,
    CircularProgress,
    List,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    TextField,
    FormControl,
} from "@mui/material/";
import {
    FolderOutlined as FolderOutlinedIcon,
    FolderSpecialOutlined as FolderSpecialOutlinedIcon,
    Check as CheckIcon,
    RotateLeft as RotateLeftIcon,
    ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import actions from "../Store/actions";
import styles from "./styles";
import { useToaster } from "../common/hooks";
import { format } from "../common/lib";
import OutdatedAudits from "./OutdatedAudits";
import useOutdatedAudits from "./OutdatedAudits/useOutdatedAudits";
import DuplicatedAuditsAlert from "./DuplicatedAuditsAlert";
import auditsUtils from "./auditsUtils";
import SearchIcon from "@mui/icons-material/Search";
import { logToSentry } from "../config/Sentry";

const Audits = ({ defaultLoading = false, defaultCleanupAudits = true }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const toast = useToaster();
    const dispatch = useDispatch();
    const theme = useTheme();
    const sxStyles = styles(theme);
    const outdatedAudits = useOutdatedAudits();
    const { auditLocations, audits } = useSelector((state) => state);

    const [cleanupAudits, setCleanupAudits] = useState(defaultCleanupAudits);
    const [loading, setLoading] = useState(defaultLoading);
    const [loadingAudit, setLoadingAudit] = useState(false);
    const [alertModal, setAlertModal] = useState(false);
    const [duplicatedAssignments, setDuplicatedAssignments] = useState([]);
    const [assetsData, setAssetsData] = useState({});
    const [initialRefreshDone, setInitialRefreshDone] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const isMounted = useRef(true);

    // Sorted and filtered audits data
    const auditsData = useMemo(() => {
        const sortedData = [...audits.data].sort((a, b) => {
            const aBranch = a.brandOperation?.branchType ?? Infinity;
            const bBranch = b.brandOperation?.branchType ?? Infinity;
            return aBranch - bBranch;
        });

        const res = [];
        const refList = new Set();
        sortedData.forEach((item) => {
            const ref = item.auditReference.toLowerCase();
            if (ref !== "none" && item.brandOperation?.branchType === 1) {
                refList.add(ref);
                res.push(item);
            }
            if (ref === "none" || !refList.has(ref)) {
                res.push(item);
                refList.add(ref);
            }
        });
        return res;
    }, [audits.data]);

    // Initial data fetch
    useEffect(() => {
        let isMounted = true; // Track if component is still mounted

        const fetchData = async () => {
            await onRefresh();
            if (isMounted) {
                setInitialRefreshDone(true); // Only update state if mounted
            }
        };

        fetchData();

        return () => {
            isMounted = false; // Mark component as unmounted in cleanup
        };
    }, []);

    // Fetch assets data for each audit after initial refresh
    useEffect(() => {
        if (!initialRefreshDone) return; // Only fetch assets data after initial refresh

        const fetchAssetsData = async () => {
            try {
                const {
                    fetchGroupAuditsDetails,
                    fetchAuditProgressDetails,
                    fetchMakeDetails,
                } = auditsUtils;
                const newAssetsData = {};
                for (const item of auditsData) {
                    const isGroupAudit =
                        item.auditReference.toLowerCase() !== "none";
                    const res = isGroupAudit
                        ? await fetchGroupAuditsDetails(audits, item.id) // Use regular function
                        : await fetchAuditProgressDetails(audits, item.id); // Use regular function

                    // Fetch make details
                    let makeName = "";
                    let cityName = "";
                    if (item.brandOperation?.make) {
                        const currentLocation = auditLocations.data.find(
                            (aLocation) => aLocation.audit.id === item.id
                        );
                        const locName =
                            currentLocation.location.name.split(",");
                        cityName = locName[locName.length - 3];

                        try {
                            makeName = await fetchMakeDetails(
                                item.organisation.id,
                                item.brandOperation.make
                            );
                        } catch (error) {
                            console.error(
                                "Error fetching make details:",
                                error
                            );
                        }
                    }

                    if (isMounted.current) {
                        newAssetsData[item.id] = {
                            makeName,
                            cityName,
                            assigned: isGroupAudit
                                ? res.groupedAssignedAsset
                                : res.assigned,
                            required: isGroupAudit
                                ? res.groupedRequired
                                : res.required,
                            unexpected: isGroupAudit
                                ? res.groupedUnexpectedAsset
                                : res.alt,
                        };
                    }
                }
                if (isMounted.current) {
                    setAssetsData(newAssetsData);
                }
            } catch (error) {
                console.error(error);
                logToSentry("UE : Fetch Asset data", {
                    error,
                    errStr: error.toString(),
                });
            }
        };
        fetchAssetsData();
    }, [auditsData, auditLocations, initialRefreshDone]);

    const filteredAuditsData = useMemo(() => {
        if (!searchQuery) return auditsData;

        const lowerCaseQuery = searchQuery.toLowerCase();

        return auditsData.filter((audit) => {
            const auditName = audit.name.toLowerCase();
            // const dueDate = format.dateTime(audit.endDate).toLowerCase();
            const brandOperationCode =
                audit.brandOperation?.brandOperationCode.toLowerCase() || "";

            const assetData = assetsData[audit.id];
            const makeName = assetData?.makeName?.toLowerCase() || "";
            const cityName = assetData?.cityName?.toLowerCase() || "";

            return (
                auditName.includes(lowerCaseQuery) ||
                // dueDate.includes(dueDate) ||
                brandOperationCode.includes(lowerCaseQuery) ||
                makeName.includes(lowerCaseQuery) ||
                cityName.includes(lowerCaseQuery)
            );
        });
    }, [auditsData, assetsData, searchQuery]);

    const highlightText = (text, query) => {
        if (!query) return text;

        const regex = new RegExp(`(${query})`, "gi");
        return text.split(regex).map((part, index) =>
            regex.test(part) ? (
                <strong style={{ textDecoration: "underline" }} key={index}>
                    {part}
                </strong>
            ) : (
                part
            )
        );
    };

    // Refresh audits data
    const onRefresh = async () => {
        setLoading(true);
        try {
            const { auditIds } = await dispatch(
                actions.auditAssignments.create.statusSelectRequest()
            );
            if (auditIds?.length) {
                await Promise.all(
                    auditIds.map((id) =>
                        dispatch(
                            actions.auditUpload.create.partialSubmitRequest(
                                id,
                                null
                            )
                        ).then(() =>
                            dispatch(
                                actions.auditDownload.create.cleanupRequest(id)
                            )
                        )
                    )
                );
            }

            setCleanupAudits(false);
            setLoading(true);
            const { audits, downloadStatus } = await dispatch(
                actions.auditDownload.create.postRequest()
            );

            const duplicatedAudits =
                downloadStatus?.filter((item) => item.isDownloaded) || [];
            setDuplicatedAssignments(duplicatedAudits);
            setAlertModal(duplicatedAudits.length > 0);

            toast.info(
                t("messages.auditsDownloadSuccess", {
                    count: audits?.length || 0,
                }),
                2000
            );
        } catch (error) {
            console.error(error);
            logToSentry("on Refersh", {
                error,
                errStr: error.toString(),
            });
            toast.error(t("errors.auditDownloadGet"));
        } finally {
            if (isMounted.current) {
                await dispatch(actions.auditAssignments.create.selectRequest());
                setLoading(false);
            }
        }
    };

    // Get loading message based on state
    const getLoadingMessage = () => {
        if (cleanupAudits) return t("messages.suspendedAudits");
        if (loadingAudit) return t("messages.loadingAudit");
        return t("messages.auditsBeingLoaded");
    };

    const handleAuditItemPress = (audit) => async () => {
        const { id, auditReference } = audit;

        try {
            const url =
                auditReference.toLowerCase() === "none"
                    ? `/auth/audits/${id}/details`
                    : `/auth/audits/${id}/groupedAudits`;

            setLoading(true);
            setLoadingAudit(true);

            dispatch(actions.audits.create.itemSet(audit));
            await dispatch(actions.typesList.create.selectRequest());
            navigate(url);
        } catch (error) {
            console.error(error);
            logToSentry("Selecting audit item", {
                error,
                errStr: error.toString(),
            });
            toast.error(t("errors.auditItemSelection"));
        }
        setLoading(false);
        // setLoadingAudit(false);
    };

    // Render loading state
    if (loading) {
        return (
            <Box sx={sxStyles.spinnerContainer}>
                <CircularProgress color="primary" />
                <Typography
                    color="primary"
                    component="p"
                    sx={sxStyles.loadingText}
                >
                    {getLoadingMessage()}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <DuplicatedAuditsAlert
                alertModal={alertModal}
                setAlertModal={setAlertModal}
                duplicatedAssignments={duplicatedAssignments}
            />
            {outdatedAudits.length > 0 && <OutdatedAudits />}
            <FormControl fullWidth sx={sxStyles.margin}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <IconButton>
                                <SearchIcon />
                            </IconButton>
                        ),
                    }}
                />
            </FormControl>
            {filteredAuditsData.length > 0 ? (
                <List sx={sxStyles.listLayout}>
                    {filteredAuditsData.map((audit) => (
                        <Card
                            key={audit.id}
                            onClick={handleAuditItemPress(audit)}
                            sx={sxStyles.card}
                        >
                            <Box sx={sxStyles.leftLayout}>
                                {audit.auditReference.toLowerCase() !==
                                "none" ? (
                                    <FolderSpecialOutlinedIcon color="primary" />
                                ) : (
                                    <FolderOutlinedIcon color="primary" />
                                )}
                            </Box>
                            <CardContent sx={sxStyles.rightLayout}>
                                {audit?.brandOperation && (
                                    <>
                                        <Typography
                                            variant="body1"
                                            component="div"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            {highlightText(
                                                audit.brandOperation
                                                    .brandOperationCode,
                                                searchQuery
                                            )}
                                        </Typography>
                                        {assetsData[audit.id]?.makeName && (
                                            <Typography
                                                variant="body2"
                                                component="div"
                                            >
                                                {highlightText(
                                                    assetsData[audit.id]
                                                        .makeName,
                                                    searchQuery
                                                )}
                                            </Typography>
                                        )}
                                        {assetsData[audit.id]?.cityName && (
                                            <Typography
                                                variant="body2"
                                                component="div"
                                            >
                                                {highlightText(
                                                    assetsData[audit.id]
                                                        .cityName,
                                                    searchQuery
                                                )}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                {audit.auditReference.toLowerCase() ===
                                    "none" && (
                                    <Typography
                                        variant="caption"
                                        component="div"
                                    >
                                        {highlightText(audit.name, searchQuery)}
                                    </Typography>
                                )}

                                <Typography component="div" variant="caption">
                                    {`${t("due")} - ${format.dateTime(
                                        audit.endDate
                                    )}`}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-around",
                                        mt: 1,
                                    }}
                                >
                                    <Box textAlign="center">
                                        <Chip
                                            sx={{ background: "none" }}
                                            icon={
                                                <CheckIcon
                                                    fontSize="small"
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {assetsData[audit.id]
                                                        ?.assigned || 0}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                    <Box textAlign="center">
                                        <Chip
                                            sx={{ background: "none" }}
                                            icon={
                                                <RotateLeftIcon
                                                    fontSize="small"
                                                    color="secondary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {assetsData[audit.id]
                                                        ?.required || 0}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                    <Box textAlign="center">
                                        <Chip
                                            sx={{ background: "none" }}
                                            icon={
                                                <ErrorOutlineIcon
                                                    fontSize="small"
                                                    color="warning"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {assetsData[audit.id]
                                                        ?.unexpected || 0}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </List>
            ) : (
                <Box sx={sxStyles.messageContainer} onClick={onRefresh}>
                    <Typography>{t("messages.noPendingAudits")}</Typography>
                    <Typography>{t("messages.tapToRefresh")}</Typography>
                </Box>
            )}
        </>
    );
};

export default Audits;

/** TODO
 * 1. Test cases for the audit search
 */
